/**
 * The Sankey's editor in the Plot card: its nodes, column by column.
 *
 * A node card opens in place, in the gauge dial's look. Inside: its kind,
 * then a Number's value controls for Signal and Calculation (the same
 * Signal / Calculation picker and formula editor), its label, the column it
 * sits in, and the nodes of the next column it flows into. Under the list:
 * what is wrong with the structure, in a sentence, and the diagram's one unit,
 * decimal places and the moving dashes switch (a switch, not segments: /ui).
 *
 * Changing what a node reads waits for Plot; everything else redraws what is
 * loaded (`setSankeyNodes`).
 */
import { html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import { allColumns, derivedSignals } from '../../state/store.ts';
import { plotState, setSankeyDisplay, setSankeyNodes } from '../../state/plot-state.ts';
import { plotColumnPool } from '../ship/ship-columns.ts';
import { isNumericType } from '../../utils/columns.ts';
import type { ColumnInfo, NumberAgg } from '../../api/types.ts';
import { NUMBER_AGGS } from './number-config.ts';
import { PLOT_COLORS } from './plot-constants.ts';
import {
  SANKEY_KINDS,
  SANKEY_MAX_COLUMNS,
  SANKEY_MAX_NODES,
  columnCount,
  newSankeyNode,
  nodeTitle,
  readsValue,
  sankeyProblem,
  type SankeyKind,
  type SankeyNodeState,
} from './sankey-config.ts';
import '../../ui/column-combobox.ts';
import '../../ui/plain-select.ts';
import '../../ui/unit-picker.ts';
import '../../ui/number-formula-editor.ts';

const DECIMALS = Array.from({ length: 11 }, (_, i) => ({ value: String(i), label: String(i) }));

@customElement('sankey-controls')
export class SankeyControls extends LightElement {
  private store = new StoreController(this, [plotState, allColumns, derivedSignals]);

  /** The node whose settings are open, by id; '' for none. */
  @state() private open = '';

  private numericPool(): ColumnInfo[] {
    return plotColumnPool().filter((c) => isNumericType(c.type) || c.derived);
  }

  private patch(id: string, p: Partial<SankeyNodeState>): void {
    setSankeyNodes(plotState.value.sankey_nodes.map((n) => (n.id === id ? { ...n, ...p } : n)));
  }

  private add(col: number): void {
    const nodes = plotState.value.sankey_nodes;
    if (nodes.length >= SANKEY_MAX_NODES) return;
    const node = newSankeyNode(nodes, col);
    // A new node in a column joins the only node of the next one, if there is
    // exactly one: the common case (another generator into the switchboard).
    const next = nodes.filter((n) => n.col === col + 1);
    if (next.length === 1) node.to = [next[0]!.id];
    setSankeyNodes([...nodes, node]);
    this.open = node.id;
  }

  private removeNode(id: string): void {
    if (this.open === id) this.open = '';
    setSankeyNodes(plotState.value.sankey_nodes
      .filter((n) => n.id !== id)
      .map((n) => (n.to.includes(id) ? { ...n, to: n.to.filter((t) => t !== id) } : n)));
  }

  private toggleLink(from: SankeyNodeState, to: string, on: boolean): void {
    this.patch(from.id, { to: on ? [...from.to, to] : from.to.filter((t) => t !== to) });
  }

  /** Series colour a Signal or Calculation node takes, as the chart draws it. */
  private dot(n: SankeyNodeState, nodes: SankeyNodeState[]): string {
    if (n.kind === 'sum') return 'var(--accent)';
    if (n.kind === 'rest') return 'var(--text-dim)';
    const i = nodes.filter(readsValue).indexOf(n);
    return PLOT_COLORS[i % PLOT_COLORS.length] ?? 'var(--accent)';
  }

  private summary(n: SankeyNodeState, nodes: SankeyNodeState[]): string {
    const what = n.kind === 'signal'
      ? `${n.column || 'no signal'} · ${NUMBER_AGGS.find((a) => a.value === n.agg)?.label.toLowerCase() ?? n.agg}`
      : SANKEY_KINDS.find((k) => k.value === n.kind)!.label.toLowerCase();
    const to = n.to.map((id) => nodes.find((x) => x.id === id)).filter(Boolean).map((x) => nodeTitle(x!));
    return `${what}${to.length ? ` → ${to.length > 2 ? `${to.length} nodes` : to.join(', ')}` : ''}`;
  }

  private renderBody(n: SankeyNodeState, nodes: SankeyNodeState[], cols: number): TemplateResult {
    const next = nodes.filter((x) => x.col === n.col + 1);
    const colOptions = Array.from({ length: Math.min(SANKEY_MAX_COLUMNS, cols + 1) }, (_, i) => ({ value: String(i), label: `Column ${i + 1}` }));
    return html`<div class="gauge-dial-body">
      <div class="gauge-field">
        <label>Kind</label>
        <plain-select .value=${n.kind} .options=${SANKEY_KINDS.map((k) => ({ value: k.value, label: k.label }))}
          .select=${(v: string) => this.patch(n.id, { kind: v as SankeyKind })}></plain-select>
        <div class="toggle-hint">${SANKEY_KINDS.find((k) => k.value === n.kind)!.title}.</div>
      </div>
      ${n.kind === 'formula'
        ? html`<number-formula-editor .value=${n.expression} .columns=${this.numericPool()}
            .change=${(value: string) => this.patch(n.id, { expression: value })}></number-formula-editor>`
        : n.kind === 'signal'
          ? html`
            <div class="gauge-field">
              <label>Signal</label>
              <column-combobox .columns=${this.numericPool()} .value=${n.column} placeholder="Choose signal…"
                .select=${(v: string) => this.patch(n.id, { column: v })}></column-combobox>
            </div>
            <div class="gauge-field">
              <label>Calculation</label>
              <plain-select .value=${n.agg} .options=${NUMBER_AGGS}
                .select=${(v: string) => this.patch(n.id, { agg: v as NumberAgg })}></plain-select>
            </div>`
          : nothing}
      <div class="gauge-pair">
        <div class="gauge-field">
          <label for="sankey-label-${n.id}">Label</label>
          <input type="text" id="sankey-label-${n.id}" maxlength="120" placeholder=${nodeTitle({ ...n, label: '' })} .value=${n.label}
            @input=${(e: Event) => this.patch(n.id, { label: (e.target as HTMLInputElement).value })} />
        </div>
        <div class="gauge-field">
          <label>Column</label>
          <plain-select .value=${String(n.col)} .options=${colOptions}
            .select=${(v: string) => this.patch(n.id, { col: Number(v) })}></plain-select>
        </div>
      </div>
      ${next.length
        ? html`<div class="gauge-field">
            <label>Flows into</label>
            <div class="sankey-links">
              ${next.map((t) => html`<label class="sankey-link">
                <input type="checkbox" .checked=${n.to.includes(t.id)}
                  @change=${(e: Event) => this.toggleLink(n, t.id, (e.target as HTMLInputElement).checked)} />
                <i style="background:${this.dot(t, nodes)}"></i><span>${nodeTitle(t)}</span>
              </label>`)}
            </div>
          </div>`
        : nothing}
    </div>`;
  }

  private renderNode(n: SankeyNodeState, nodes: SankeyNodeState[], cols: number): TemplateResult {
    const open = this.open === n.id;
    const toggle = (): void => { this.open = open ? '' : n.id; };
    return html`<div class="gauge-dial ${open ? 'is-open' : ''}">
      <div class="gauge-dial-head" role="button" tabindex="0" aria-expanded=${open ? 'true' : 'false'}
        @click=${toggle}
        @keydown=${(e: KeyboardEvent) => {
          if (e.target !== e.currentTarget || (e.key !== 'Enter' && e.key !== ' ')) return;
          e.preventDefault();
          toggle();
        }}>
        <i class="sankey-dot" style="background:${this.dot(n, nodes)}"></i>
        <span class="gauge-dial-name">
          <span class="gauge-dial-title">${nodeTitle(n)}</span>
          <span class="gauge-dial-sum">${this.summary(n, nodes)}</span>
        </span>
        ${nodes.length > 2
          ? html`<button class="remove-btn" title="Remove this node" @click=${(e: Event) => { e.stopPropagation(); this.removeNode(n.id); }}>×</button>`
          : nothing}
      </div>
      ${open ? this.renderBody(n, nodes, cols) : nothing}
    </div>`;
  }

  override render(): TemplateResult {
    const s = plotState.value;
    const nodes = s.sankey_nodes;
    const cols = columnCount(nodes);
    const problem = sankeyProblem(nodes);
    const full = nodes.length >= SANKEY_MAX_NODES;
    const disp = s.sankey_display;
    return html`<div class="axis-block">
      <label>Nodes <span class="toggle-hint">${nodes.length} of ${SANKEY_MAX_NODES} · left to right</span></label>
      ${Array.from({ length: cols }, (_, k) => {
        const list = nodes.filter((n) => n.col === k);
        return html`<div class="sankey-col">
          <div class="sankey-col-head">
            <span>Column ${k + 1}</span>
            ${full ? nothing : html`<button type="button" class="link-btn" title="Add a node to column ${k + 1}" @click=${() => this.add(k)}>+ Node</button>`}
          </div>
          ${list.length
            ? html`<div class="gauge-dials">${list.map((n) => this.renderNode(n, nodes, cols))}</div>`
            : html`<div class="toggle-hint sankey-col-empty">No nodes yet.</div>`}
        </div>`;
      })}
      ${cols < SANKEY_MAX_COLUMNS && nodes.some((n) => n.col === cols - 1) && !full
        ? html`<button class="btn btn-secondary gauge-add" @click=${() => this.add(cols)}>+ Add column</button>`
        : nothing}
      ${problem ? html`<div class="toggle-hint is-error sankey-problem">${problem}</div>` : nothing}
      <div class="sankey-display">
        <div class="gauge-sub">Values</div>
        <div class="gauge-pair">
          <div class="gauge-field">
            <label>Unit</label>
            <unit-picker .value=${disp.unit} .select=${(v: string) => setSankeyDisplay({ unit: v })}></unit-picker>
          </div>
          <div class="gauge-field">
            <label>Decimal places</label>
            <plain-select .value=${String(disp.decimals)} .options=${DECIMALS}
              .select=${(v: string) => setSankeyDisplay({ decimals: Number(v) })}></plain-select>
          </div>
        </div>
        <div class="toggle-hint">One unit for every node, so ribbon widths compare across the whole diagram.</div>
        <button type="button" class="dset-toggle ${disp.motion ? 'is-on' : ''}" role="switch" aria-checked=${disp.motion ? 'true' : 'false'}
          @click=${() => setSankeyDisplay({ motion: !disp.motion })}>
          <span class="dset-toggle-text">
            <strong>Moving dashes</strong>
            <span>${disp.motion ? 'On: dashes run along each ribbon, in the direction of the flow.' : 'Off: the ribbons stay still.'}</span>
          </span>
          <span class="sd-switch" aria-hidden="true"></span>
        </button>
      </div>
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sankey-controls': SankeyControls;
  }
}
